/** An app's detail — `/apps/:id` (S2).
 *
 *  From 1024px up it is a drawer over the page it was opened from (앱, or an
 *  app in 오늘's top five); below, a page of its own with a trail
 *  "앱 › name". Both have the address, so a reload or a shared link lands on
 *  the app: opened by its address on a wide screen, the app list is drawn
 *  under the drawer. Closing goes back to the page behind it, or to 앱.
 *
 *  It holds what the list has no room for: the package and the AdMob app ID
 *  (to copy), the figures over the period the list was showing (`?range=`
 *  travels with the click), and the last twelve months. */

import { useMemo, type ReactNode } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import type { StatsRow } from '@/api/client'
import type { DetailState } from '@/lib/detail'
import { addMonths, monthOf, rangeLabel, seoulToday } from '@/lib/dates'
import { count, percent, roas as roasText, usd } from '@/lib/money'
import { useApps, useStats } from '@/lib/queries'
import { useSpend } from '@/lib/useSpend'
import { platformKey, profitKey, statusOf } from '@/lib/status'
import {
  Code,
  CopyButton,
  DataTable,
  Drawer,
  EmptyState,
  ErrorState,
  KeyValue,
  Money,
  PageHeader,
  Skeleton,
  StatusPill,
  Won,
  useIsWide,
  useRange,
  type Column,
  type KeyValueItem,
} from '@/ui'
import AppList from './AppList'

function useDetailMode(overlay: boolean) {
  const wide = useIsWide()
  const navigate = useNavigate()
  const location = useLocation()
  const bg = (location.state as DetailState | null)?.background
  const backTo = bg ? `${bg.pathname}${bg.search}` : `/apps${location.search}`
  return {
    mode: (wide ? 'drawer' : 'page') as 'drawer' | 'page',
    withList: wide && !overlay && !bg,
    backTo,
    close: () => (bg ? navigate(-1) : navigate(backTo)),
  }
}

export default function AppRoute({ overlay = false }: { overlay?: boolean }) {
  const id = Number(useParams().id)
  const m = useDetailMode(overlay)
  return (
    <>
      {m.withList && <AppList />}
      <AppDetail key={id} id={id} mode={m.mode} onClose={m.close} backTo={m.backTo} />
    </>
  )
}

// ── months ──────────────────────────────────────────────────────────────────

interface MonthRow extends StatsRow {
  month: string
}

function Months({ id }: { id: number }) {
  const spend = useSpend()
  const thisMonth = monthOf(seoulToday())
  const from = addMonths(thisMonth, -11)
  const q = useStats({ by: 'app_month', from, to: thisMonth })
  const rows = useMemo(
    () =>
      (q.data?.rows ?? [])
        .filter((r): r is MonthRow => r.app_id === id && !!r.month)
        .sort((a, b) => b.month.localeCompare(a.month)),
    [q.data, id],
  )
  const { shown, hint } = spend
  const columns: Column<MonthRow>[] = [
    { key: 'month', header: '월', card: 'title', cell: (r) => <span className="whitespace-nowrap tabular-nums">{r.month.replace('-', '. ')}</span> },
    { key: 'revenue', header: '수익', align: 'right', card: 'hidden', cell: (r) => <Money value={r.revenue} krw /> },
    { key: 'cost', header: '지출', align: 'right', card: 'hidden', cell: (r) => <Money value={r.cost} hidden={!shown} hint={hint} /> },
    { key: 'profit', header: '순이익', align: 'right', card: 'hidden', cell: (r) => <Money value={r.profit} hidden={!shown} hint={hint} /> },
    { key: 'impressions', header: '노출', align: 'right', card: 'hidden', cell: (r) => <span className="tabular-nums">{count(r.impressions)}</span> },
    { key: 'ecpm', header: 'eCPM', align: 'right', card: 'hidden', cell: (r) => <Money value={r.ecpm} /> },
    // The phone's card: one line instead of five label rows.
    {
      key: 'summary',
      header: '요약',
      cardOnly: true,
      card: 'sub',
      cell: (r) => (
        <span className="tabular-nums">
          수익 <span className="whitespace-nowrap text-foreground">{usd(r.revenue)}</span><Won usd={r.revenue} className="ml-1" />
          {shown && (
            <>
              {' · '}순이익 <span className="whitespace-nowrap text-foreground">{usd(r.profit)}</span>
            </>
          )}
          {' · '}노출 {count(r.impressions)}
        </span>
      ),
    },
  ]
  return (
    <section aria-label="월별" className="space-y-2">
      <h3 className="text-section font-semibold">
        월별<span className="ml-2 text-meta font-normal text-muted-foreground">최근 12개월</span>
      </h3>
      <DataTable
        label="월별 수익"
        columns={columns}
        rows={rows}
        rowKey={(r) => r.month}
        loading={q.isLoading}
        error={q.error}
        onRetry={() => q.refetch()}
        // A table in the desktop drawer (the viewport is wide even when the
        // drawer is not); cards on the phone, where the drawer is the page.
        empty={<EmptyState title="최근 12개월에 이 앱의 수익이 없다" />}
      />
    </section>
  )
}

// ── the app ─────────────────────────────────────────────────────────────────

function AppDetail({ id, mode, onClose, backTo }: { id: number; mode: 'drawer' | 'page'; onClose: () => void; backTo: string }) {
  const apps = useApps()
  const { range, from, to } = useRange('30d')
  const spend = useSpend()
  const period = useStats({ by: 'app', from, to, include_zero: true })
  const info = apps.data?.find((a) => a.id === id)
  const row = period.data?.rows.find((r) => r.app_id === id)
  const name = info?.name ?? row?.app_name
  const platform = info?.platform ?? row?.platform
  const packageName = info?.package_name ?? row?.package_name ?? ''
  const admobId = info?.admob_app_id ?? row?.admob_app_id ?? ''
  const { shown, hint } = spend

  const missing = !apps.isLoading && !period.isLoading && !info && !row
  const m = row ?? { revenue: 0, cost: 0, profit: 0, roas: 0, impressions: 0, clicks: 0, ad_requests: 0, matched_requests: 0, ctr: 0, ecpm: 0, match_rate: 0 }

  const ids: KeyValueItem[] = [
    {
      label: '패키지',
      value: packageName ? (
        <span className="flex items-center gap-2">
          <Code>{packageName}</Code>
          <CopyButton text={packageName} what="패키지" />
        </span>
      ) : (
        '—'
      ),
    },
    {
      label: 'AdMob 앱 ID',
      value: admobId ? (
        <span className="flex items-center gap-2">
          <Code>{admobId}</Code>
          <CopyButton text={admobId} what="AdMob 앱 ID" />
        </span>
      ) : (
        '—'
      ),
    },
  ]
  const figures: KeyValueItem[] = [
    { label: '수익', value: <Money value={m.revenue} krw /> },
    { label: '지출', value: <Money value={m.cost} hidden={!shown} hint={hint} /> },
    { label: '순이익', value: <Money value={m.profit} hidden={!shown} hint={hint} /> },
    { label: 'ROAS', value: shown ? <span className="tabular-nums">{roasText(m.roas, m.cost)}</span> : <Money value={null} hidden hint={hint} /> },
    { label: '노출', value: <span className="tabular-nums">{count(m.impressions)}</span> },
    { label: 'eCPM', value: <Money value={m.ecpm} /> },
    { label: '매칭률', value: <span className="tabular-nums">{m.ad_requests > 0 ? percent(m.match_rate) : '—'}</span> },
  ]

  const meta: ReactNode = (
    <>
      {platform && <span>{statusOf('platform', platformKey(platform)).label}</span>}
      {shown && row && <StatusPill domain="profit" status={profitKey(row)} />}
    </>
  )

  let body: ReactNode
  if (apps.error || period.error) {
    body = <ErrorState title="앱 정보를 불러오지 못했다" error={apps.error ?? period.error} onRetry={() => { apps.refetch(); period.refetch() }} />
  } else if (missing) {
    body = <EmptyState title="찾을 수 없는 앱" description="앱 목록에 없는 주소다." />
  } else if (apps.isLoading && period.isLoading) {
    body = <Skeleton className="h-40 w-full" />
  } else {
    body = (
      <>
        <KeyValue items={ids} stack />
        <section aria-label="선택한 기간" className="space-y-2">
          <h3 className="text-section font-semibold">
            {rangeLabel(range)}
            <span className="ml-2 text-meta font-normal text-muted-foreground">{from === to ? from : `${from} ~ ${to}`}</span>
          </h3>
          <KeyValue items={figures} />
          {/* The drawer says why spend is a dash, once; the cells only dash. */}
          {!shown && spend.ready && hint && <p className="text-meta text-muted-foreground">{hint}</p>}
          {shown && <p className="text-meta text-muted-foreground">지출은 이 앱에 연결된 캠페인만 센다.</p>}
        </section>
        <Months id={id} />
      </>
    )
  }

  const title = name ?? '앱'
  if (mode === 'drawer') {
    return (
      <Drawer open onClose={onClose} title={title} meta={meta}>
        <div className="space-y-5">{body}</div>
      </Drawer>
    )
  }
  return (
    <>
      <PageHeader title={title} parent={{ to: backTo.startsWith('/apps') ? backTo : '/apps', label: '앱' }} crumb={name} docTitle={name}>
        <div className="flex flex-wrap items-center gap-2 text-meta text-muted-foreground">{meta}</div>
      </PageHeader>
      <div className="space-y-5">{body}</div>
    </>
  )
}
